import React, { FC, useEffect, useState } from 'react';
import { Route, BrowserRouter, Outlet, Routes, useLocation, useNavigate } from 'react-router-dom';
import { NavBar, Space } from 'antd-mobile';

// Import feature components
import { ClientsPage, ClientPage } from '../clients';
import { PlanningPage } from '../planning';
import { InterventionPage } from '../interventions';
import { Account } from '../account';
import { Photo } from '../photo';
import { Login } from '../auth';
import { Contact } from '../contact';
import { LivretDeBord, LivretConsignes, LivretNettoyeurs } from '../livret';
import { BonIntervention } from '../bon';
import { DemoPage } from '../demo';
import { HomePage, NewInterventionChoice, ClientNewChoice, WhatsNewPopup } from '../home';
import { BlockedClientsPage } from '../blocked';
import { BottomBar } from '../navigation';
// Import hooks and utilities
import { useAuth } from '../../../hooks/use-auth';
import { useNavbar } from '../../../hooks/use-navbar';
import { useTheme } from '../../../hooks/use-theme';
import { useFontScale, FontScale } from '../../../hooks/use-font-scale';
import { hasFeature } from '../../../types/features';
import { UserJson } from '../../../types/models';
import { faroLogger } from '../../../observability/faro-logger';

const HelpButton: FC = () => {
  const navigate = useNavigate();
  return (
    <span
      role="button"
      aria-label="Aide / Démo"
      onClick={() => navigate('/demo')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'rgba(49,220,200,0.16)',
        color: '#31DCC8',
        fontSize: 14,
        fontWeight: 800,
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        border: '1px solid rgba(49,220,200,0.3)',
      }}
    >
      ?
    </span>
  );
};

const ThemeToggleButton: FC = () => {
  const { theme, toggle } = useTheme();
  return (
    <span
      role="button"
      aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
      onClick={() => {
        faroLogger.info('theme_changed', {
          theme: theme === 'dark' ? 'light' : 'dark',
          source: 'navbar_toggle',
        });
        toggle();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
        color: theme === 'dark' ? '#F59E0B' : '#FFD580',
        fontSize: 15,
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.22)',
        lineHeight: 1,
      }}
    >
      {theme === 'dark' ? '☀' : '🌙'}
    </span>
  );
};

const FontSizeButton: FC = () => {
  const { scale, setScale } = useFontScale();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const options: { key: FontScale; label: string; size: number }[] = [
    { key: 'normal', label: 'Normal', size: 14 },
    { key: 'moyen', label: 'Moyen', size: 16 },
    { key: 'grand', label: 'Grand', size: 18 },
  ];
  // Couleurs explicites (opaques) selon le thème : les variables CSS pouvaient
  // donner un panneau translucide illisible.
  const dark = theme === 'dark';
  const panelBg = dark ? '#13233c' : '#ffffff';
  const panelBorder = dark ? 'rgba(255,255,255,0.16)' : '#e2e8f0';
  const headColor = dark ? '#94a3b8' : '#64748b';
  const itemColor = dark ? '#ffffff' : '#0f172a';
  return (
    <span style={{ display: 'inline-flex' }}>
      <span
        role="button"
        aria-label="Taille du texte"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          color: '#fff',
          fontWeight: 800,
          cursor: 'pointer',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          border: '1px solid rgba(255,255,255,0.22)',
          lineHeight: 1,
        }}
      >
        <span style={{ fontSize: 11 }}>A</span>
        <span style={{ fontSize: 15 }}>A</span>
      </span>
      {open && (
        <>
          {/* Fond cliquable plein écran pour refermer */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
          <div
            style={{
              position: 'fixed',
              top: 52,
              right: 12,
              zIndex: 9999,
              minWidth: 180,
              background: panelBg,
              border: `1px solid ${panelBorder}`,
              borderRadius: 12,
              padding: 6,
              boxShadow: '0 14px 34px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: headColor,
                padding: '4px 8px 6px',
              }}
            >
              Taille du texte
            </div>
            {options.map((o) => {
              const active = scale === o.key;
              return (
                <div
                  key={o.key}
                  role="button"
                  onClick={() => {
                    setScale(o.key);
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: active ? 'rgba(49,220,200,0.18)' : 'transparent',
                    color: active ? '#0f9b8c' : itemColor,
                    fontWeight: active ? 800 : 600,
                  }}
                >
                  <span style={{ fontSize: o.size }}>{o.label}</span>
                  {active && <span style={{ fontSize: 14 }}>✓</span>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
};

const HomeButton: FC = () => {
  const navigate = useNavigate();
  return (
    <span
      role="button"
      aria-label="Accueil"
      onClick={() => navigate('/')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
        color: '#fff',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.22)',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4M9 22V12h6v10" />
      </svg>
    </span>
  );
};

const MyNavBar: FC = () => {
  const navbar = useNavbar();
  const location = useLocation();
  const onDemoPage = location.pathname === '/demo';
  const onHomePage = location.pathname === '/';
  return (
    <NavBar
      back={navbar?.back}
      backArrow={navbar?.backArrow}
      onBack={() => {
        navbar && navbar.onBack && navbar.onBack();
      }}
      right={
        onDemoPage ? (
          navbar?.right
        ) : (
          <Space style={{ '--gap': '8px', alignItems: 'center' } as React.CSSProperties}>
            {navbar?.right}
            <ThemeToggleButton />
            <FontSizeButton />
            {!onHomePage && <HomeButton />}
            <HelpButton />
          </Space>
        )
      }
    >
      {navbar?.title}
    </NavBar>
  );
};

const Main: FC<any> = () => {
  const auth = useAuth();
  useEffect(() => {
    auth.autoSignIn();
  }, []);

  function Layout(): JSX.Element {
    const location = useLocation();
    const isDemo = location.pathname === '/demo';
    return (
      <div className="app" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <WhatsNewPopup />
        <div className="top" style={{ flexShrink: 0 }}>
          <MyNavBar />
        </div>
        <div
          className="body"
          style={{ flexGrow: 1, overflowY: isDemo ? 'hidden' : 'auto', minHeight: 0 }}
        >
          <div className="zoomable" style={isDemo ? { height: '100%' } : undefined}>
            <Outlet />
            {!isDemo && (
              <div style={{ height: 'calc(60px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
            )}
          </div>
        </div>
        {!isDemo && (
          <div className="bottom" style={{ flexShrink: 0 }}>
            <BottomBar features={hasFeature(auth.user as UserJson)} />
          </div>
        )}
      </div>
    );
  }

  return auth.user ? (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="new-intervention" element={<NewInterventionChoice />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="me" element={<Account />} />
          <Route path="photo" element={<Photo />} />
          <Route path="contact" element={<Contact />} />
          <Route path="livret" element={<LivretDeBord />} />
          <Route path="livret/consignes/:section" element={<LivretConsignes />} />
          <Route path="livret/nettoyeurs" element={<LivretNettoyeurs />} />
          <Route path="demo" element={<DemoPage />} />
          <Route path="clients-bloques" element={<BlockedClientsPage />} />
          <Route path="client/:clientId/bon" element={<BonIntervention />} />
          <Route path="planning" element={<PlanningPage />} />

          <Route path="client/:clientId" element={<ClientPage />} />
          <Route path="client/:clientId/new-choice" element={<ClientNewChoice />} />
          <Route path="client/:clientId/new" element={<InterventionPage />} />
          <Route path="client/:clientId/intervention/:interventionId" element={<InterventionPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  ) : auth.user === false ? (
    <Login />
  ) : (
    <></>
  );
};

export default Main;
